import { Country, OrganizationRecord, OrganizationType } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiSheet, useAppForm } from 'noddi-ui-web';
import { useEffect } from 'react';
import { z } from 'zod';
import { invalidateOrganizationQueries } from './invalidateOrganizationQueries';
import { TanstackOrganizationTypeSelect } from './TanstackOrganizationTypeSelect';
import { formatRegistrationNumber, validateNorwegianOrganizationNumber } from './utils';

const FORM_ID = 'edit-organization-general-form';
const ORGANIZATION_TYPE_VALUES = Object.values(OrganizationType) as [OrganizationType, ...OrganizationType[]];

const createValidationSchema = (country: Country) =>
  z
    .object({
      name: z.string().min(1, 'Required'),
      nameLegal: z.string(),
      registrationNumber: z.string().min(1, 'Required'),
      type: z.enum(ORGANIZATION_TYPE_VALUES)
    })
    .superRefine((value, ctx) => {
      if (country !== Country.NOR) {
        return;
      }

      const registrationNumberError = validateNorwegianOrganizationNumber(value.registrationNumber);
      if (registrationNumberError) {
        ctx.addIssue({
          code: 'custom',
          message: 'Invalid Norwegian organization number',
          path: ['registrationNumber']
        });
      }
    });

type FormValues = z.input<ReturnType<typeof createValidationSchema>>;

type Props = {
  open: boolean;
  onClose: () => void;
  organization: OrganizationRecord;
  organizationId: number;
};

export const EditOrganizationGeneralSheet = ({ open, onClose, organization, organizationId }: Props) => {
  const { mutateAsync: patchOrganization } = noddiAsync.usePatch({
    type: URLKeys.patchOrganization,
    queryConfig: {
      onSuccess: () => {
        invalidateOrganizationQueries(organizationId);
        onClose();
      }
    }
  });

  const getDefaultValues = (): FormValues => ({
    name: organization.name,
    nameLegal: organization.nameLegal ?? '',
    registrationNumber: formatRegistrationNumber(organization.registrationNumber ?? ''),
    type: organization.type.value
  });

  const form = useAppForm({
    defaultValues: getDefaultValues(),
    validators: {
      onSubmit: createValidationSchema(organization.country.code)
    },
    onSubmit: async ({ value }) => {
      await patchOrganization({
        id: organizationId,
        name: value.name.trim(),
        nameLegal: value.nameLegal?.trim() || null,
        registrationNumber: value.registrationNumber.replace(/\s/g, ''),
        type: value.type
      });
    }
  });

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getDefaultValues is a new closure each render and would reset on every render; reset only on open or when the fields it reads change
  }, [open, form, organization.name, organization.nameLegal, organization.registrationNumber, organization.type.value]);

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Edit general details'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />}
      >
        <form
          id={FORM_ID}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className='flex flex-col gap-5'>
            <form.AppField name='name'>{(field) => <field.Input label='Organization name' required />}</form.AppField>
            <form.AppField name='nameLegal'>{(field) => <field.Input label='Legal name' />}</form.AppField>
            <form.AppField name='registrationNumber'>
              {(field) => (
                <field.Input
                  label='Registration number'
                  required
                  onChange={(event) => {
                    form.setFieldValue('registrationNumber', formatRegistrationNumber(event.target.value));
                  }}
                />
              )}
            </form.AppField>
            <form.AppField name='type'>{() => <TanstackOrganizationTypeSelect />}</form.AppField>
          </div>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
